import type {App} from "vue";
import type {ToastKind, ToastService} from "@/types/toast";
import {createApp} from 'vue';
import ToastManager from './ToastManager.vue';
import {v4 as uuidv4} from 'uuid';
import {ToastType} from "@/components/toast/ToastType";

let toastInstance = null as unknown as ToastService;

const ToastPlugin = {
    install(app: App) {
        const ToastConstructor = createApp(ToastManager);
        const instance = ToastConstructor.mount(document.createElement('div')) as InstanceType<typeof ToastManager>;
        document.body.appendChild(instance.$el);

        const durationsByType = {
            [ToastType.ALERT]: 5_000,
            [ToastType.ERROR]: 5_000,
            [ToastType.SUCCESS]: 3_000,
            [ToastType.INFO]: 3_000,
            [ToastType.LOADING]: 0
        };

        app.config.globalProperties.$toast = {
            show(description, title = '', type = ToastType.INFO, duration = durationsByType[type]) {
                const id = uuidv4();
                instance.addToast({description, title, type, duration, id});
                return {id};
            },

            alert(description, title, duration = durationsByType[ToastType.ALERT]) {
                return this.show(description, title, ToastType.ALERT, duration);
            },

            success(description, title = 'Success', duration = durationsByType[ToastType.SUCCESS]) {
                return this.show(description, title, ToastType.SUCCESS, duration);
            },

            error(description, title = 'Error', duration = durationsByType[ToastType.ERROR]) {
                return this.show(description, title, ToastType.ERROR, duration);
            },

            info(description, title = 'Info', duration = durationsByType[ToastType.INFO]) {
                return this.show(description, title, ToastType.INFO, duration);
            },

            loading(description, duration = durationsByType[ToastType.LOADING]) {
                const loadingToast = this.show(description, '', ToastType.LOADING, duration);

                let isActive = true;

                const timeoutId = setTimeout(() => {
                    if (!isActive) return;
                    this.update(loadingToast.id, {
                        type: ToastType.ERROR,
                        description: 'Loading timed out'
                    });
                }, 10_000);

                const originalUpdate = this.update.bind(this);
                this.update = (id, updatedProps) => {
                    if (id === loadingToast.id && updatedProps.type && updatedProps.type !== ToastType.LOADING) {
                        isActive = false;
                        clearTimeout(timeoutId);
                    }
                    return originalUpdate(id, updatedProps);
                };

                return loadingToast;
            },

            update(id, updatedProps) {
                const defaultProps = {
                    title: titleByType(updatedProps.type, updatedProps.title),
                    duration: updatedProps.duration || durationsByType[updatedProps.type!] || 3000,
                }

                instance.updateToast(id, {...defaultProps, ...updatedProps});
            }
        };

        toastInstance = app.config.globalProperties.$toast;
    },
};

function titleByType(type: ToastKind | undefined, title: string | undefined) {
    if (title) {
        return title;
    }

    switch (type) {
        case ToastType.ALERT:
            return 'Alert';
        case ToastType.ERROR:
            return 'Error';
        case ToastType.SUCCESS:
            return 'Success';
        case ToastType.INFO:
            return 'Info';
        default:
            return '';
    }
}

export {toastInstance};
export default ToastPlugin;